import React, { useState, useEffect } from 'react';
import { useFormik } from "formik";
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import {
    SlDrawer, SlInput, SlIcon, SlTooltip,
    SlIconButton, SlButton
} from '@shoelace-style/shoelace/dist/react';
import ScrollContainer from 'react-indiana-drag-scroll';

import api from "../../services/api";
import { debounce } from '../../utils/Debounce';
import { formatDateWithTime } from '../../utils/dateFormat';
import { Toast } from '../../shared/Toast';
import NoDataFound from '../../shared/NoDataFound';
import TableLoader from '../../shared/TableLoader';
import Paginator from '../../shared/Paginator';
import config from '../../config';
import PendingRequestModal from '../MakerChecker/components/PendingRequestModal';

const initialContentWriter = {
    name: '',
    email: '',
    mobile: '',
    password: '',
};

const ContentWriterSchema = Yup.object().shape({
    name: Yup.string()
    .matches(/^[a-zA-Z][a-zA-Z0-9_ ]*$/, "Must start with a letter; only letters, numbers, spaces, and '_' allowed")
    .required('Name is required'),  
    email: Yup.string().email().required('Email is required'),
    mobile: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Invalid Mobile number")
        .required("Mobile Number is required"),
    password: Yup.string()
        .matches(/^[a-zA-Z0-9][a-zA-Z0-9\./!@#$%^&*-?+~]*$/, "Password should not contain white space")
        .required("Password is required"),
});

function ContentWriter() {
    const navigate = useNavigate();
    const HeaderSearch = useSelector(state => state.Head.adssearch);

    const [data, setData] = useState([]);
    const [show, setShow] = useState(false);
    const [loader, setLoader] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [flag, setFlag] = useState('');
    const [id, setId] = useState('');
    const [search, setSearch] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const [selectedMcId,setSelectedMcId] = useState('');

    const handleClose = () => {
        setShow(false);
        setSubmit(false);
        formik.resetForm();
    };
    const handleCloseModal = () => {
        setSelectedMcId(null);
    };
    const handleShow = (type, item = {}) => {
        if (type === "add") {
            formik.setValues(initialContentWriter);
        } else if (type === "edit") {
            formik.setValues({
                name: item.name,
                email: item.email,
                mobile: item.mobile,
                password: item.password,
                status: +item.status,  // Set status for edit only
            });
            setId(item.content_writer_id);
        }
        setFlag(type);
        setShow(true);
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
        loadData(search, event.selected);
    };

    const loadData = async (searchString = '', page = 0) => {
        try {
            setLoader(true);
            const res = await api({
                url: `/user/content_writer/getContentWriters`,  // Changed API endpoint
                method: "POST",
                data: { page, limit: config.PER_PAGE, search: searchString },
            });
            setData(res.data.data || []);
            setTotalPages(Math.ceil(res.data.total / config.PER_PAGE));
            setLoader(false);
        } catch (error) {
            setLoader(false);
            if (error.response?.status === 401) navigate("/");
        }
    };

    const Header = () => {
        setSearch(HeaderSearch);
        setCurrentPage(0);
        loadData(HeaderSearch, 0);
    };

    useEffect(() => {
        Header();
    }, [HeaderSearch]);

    const searchData = (e) => {
        setSearch(e.target.value);
        setCurrentPage(0);
        loadData(e.target.value, 0);
    };

    const handleRequestClose = (event) => {
        if (event.detail.source === 'overlay') event.preventDefault();
    };

    const formik = useFormik({
        initialValues: initialContentWriter,
        validationSchema: ContentWriterSchema,
        onSubmit: async (values) => {
            try {
                const { name, email, mobile, password, status } = values;
                const payload = flag === 'edit'
                    ? { name, email, mobile, password, status: +status } 
                    : { name, email, mobile, password };

                const url = flag === 'add' ? `/user/content_writer/` : `/user/content_writer/${id}`;  

                const res = await api({
                    url,
                    method: "POST",
                    data: {...payload,ref_table:'content_writer'},
                });
                if (res.code !== 200) {
                    Toast("error", res.data.message);
                } else {
                    Toast("success", `Content Creator ${flag === 'add' ? 'created' : 'updated'} request sent successfully`);
                    handleClose();
                    loadData(search, currentPage);
                }
            } catch (error) {
                Toast("error", error?.response?.message || "Something went wrong");
                if (error.response?.status === 401) navigate("/");
            }
        }
    });

    return (
        <>
            <div className='flex gap-4 justify-end mb-4'>
                {/* <SlInput size="small" onSlInput={debounce(searchData, 500)} placeholder="Search By Name / Email / Mobile..">
                    <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
                </SlInput> */}
                <SlButton size="small" variant="primary" onClick={() => handleShow("add")}>Add Content Writer</SlButton>
            </div>

            <SlDrawer label={`${flag === 'edit' ? 'Edit' : 'Create'} Content Creator`} open={show} onSlRequestClose={handleRequestClose} onSlAfterHide={handleClose}>
                <form onSubmit={formik.handleSubmit}>
                    {['name', 'email', 'mobile', 'password'].map((field) => (
                        <div className="mb-3" key={field}>
                            <label className="mb-1">{field.charAt(0).toUpperCase() + field.slice(1)}</label><span className="text-red-600">*</span>
                            <input
                                type={field === 'email' ? 'email' : 'text'}
                                className="form-control"
                                name={field}
                                placeholder={`Enter ${field}`}
                                value={formik.values[field]}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                            />
                            {submit && formik.errors[field] && (
                                <div className="text-red-600">{formik.errors[field]}</div>
                            )}
                        </div>
                    ))}

                    {flag === 'edit' && (
                        <div className="form-check form-switch mb-3">
                            <label className="form-check-label">Status</label>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={formik.values.status}
                                onChange={(e) => formik.setFieldValue('status', e.target.checked)}
                            />
                        </div>
                    )}

                    <div slot="footer">
                        <SlButton type="submit" variant="primary" size="small" onClick={() => setSubmit(true)}>
                            Submit
                        </SlButton>
                    </div>
                </form>
            </SlDrawer>
            <div className='mb-4'>
                <ScrollContainer>
                    <table className='table table-bordered'>
                        <thead className="sticky top-0">
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Mobile</th>
                                <th>Password</th>
                                <th>Status</th>
                                <th>Created On</th>
                                <th>Created By</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        {(data.length && !loader) ? (
                            <tbody>
                                {data.map((item) => (
                                    <tr key={item.content_writer_id}>
                                        <th scope="row" className="flex items-center justify-center text-center gap-2">
                                            {item?.mc_actions?.length ?<button className="h-2 w-2 px-1 py-1 rounded-full bg-orange-300" title={`pending edit action by ${item?.mc_actions?.[0]?.mc_user_name}`} onClick={() => setSelectedMcId(item?.mc_actions?.[0]?.mc_id)}></button>: null}
                                            <span>{item.name}</span>
                                        </th>
                                        <td>{item.email}</td>
                                        <td>{item.mobile}</td>
                                        <td>{item.password}</td>
                                        <td>
                                            <span className={item.status ? "full-badge text-center" : "unpaid-badge text-center"}>
                                                {item.status ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>{formatDateWithTime(item.created_date)}</td>
                                        <td>{item.b2b_name || '-'}</td>
                                        <td>
                                            <SlTooltip content="Edit Content Creator">
                                                <SlIconButton
                                                    library="lucide"
                                                    name="edit"
                                                    onClick={() => handleShow("edit", item)}
                                                />
                                            </SlTooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        ) : !data.length && !loader ? (
                            <NoDataFound colspan="8" />
                        ) : (
                            <TableLoader />
                        )}
                    </table>
                </ScrollContainer>
                {totalPages > 1 && (
                    <Paginator pageCount={totalPages} onPageChange={handlePageClick} />
                )}
                <PendingRequestModal mc_id={selectedMcId} isOpen={!!selectedMcId} onClose={handleCloseModal} />
            </div>
        </>
    );
}

export default ContentWriter;
